import React, { useState, useEffect, useLayoutEffect } from "react";
import styled from "styled-components";
import { DataEntry } from "./DataEntry";
import { SlidingPane } from "./generic-components/SlidingPane";
import { AnalysisView } from "./AnalysisView";
import {
  PreferencesProvider,
  usePreferences,
  ThemeProps,
} from "./usePreferences";
import { AppHeader } from "./AppHeader";
import { HelpTour } from "./tour/HelpTour";
import {
  setTourToComplete,
  hasTourBeenCompleted,
} from "./local-storage/localStorage.helpers";
import { useWindowSize } from "./common-components/useWindowSize";

type ContainerProps = {
  preventScrolling?: boolean;
  windowSize: { width: number | undefined; height: number | undefined };
} & ThemeProps;

const Container = styled.div`
  min-width: ${({ windowSize }: ContainerProps) =>
    windowSize && windowSize.width ? `${windowSize.width}px` : "100vw"};
  min-height: ${({ windowSize }: ContainerProps) =>
    windowSize && windowSize.height ? `${windowSize.height}px` : "100vh"};
  display: flex;
  font-family: ${({ theme }: ContainerProps) => theme.typography.data.family};
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  background-color: ${({ theme }: ContainerProps) => theme.palette.background};
  overflow: ${({ preventScrolling }: ContainerProps) =>
    preventScrolling ? "hidden" : "auto"};
`;

const PaneContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const AnalysisPane = styled.div`
  display: flex;
  flex-direction: column;
  width: ${({ windowSize }: ContainerProps) =>
    windowSize && windowSize.width ? `${windowSize.width}px` : "100vw"};
  height: ${({ windowSize }: ContainerProps) =>
    windowSize && windowSize.height ? `${windowSize.height}px` : "100vh"};
  box-sizing: border-box;
`;

const ReactPace = () => {
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [preferences, _dispatch] = usePreferences();
  const [canOpenPanel, setCanPanelOpen] = useState(false);
  const [isTourOpen, setTourOpen] = useState(false);
  const [tourIsComplete, setTourIsComplete] = useState(false);

  const [width, height] = useWindowSize();

  useEffect(() => {
    setTourIsComplete(hasTourBeenCompleted());
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setTourOpen(!isPanelOpen && !tourIsComplete);
    }, 700);
  }, [isPanelOpen]);

  const togglePane = (overrideCanOpen?: boolean) => {
    (canOpenPanel || overrideCanOpen) && setIsPanelOpen(!isPanelOpen);
    overrideCanOpen && setCanPanelOpen(overrideCanOpen);
  };

  const onTourComplete = () => {
    setTourOpen(false);
    setTourIsComplete(true);
    setTourToComplete();
  };

  return (
    <>
      <Container
        preventScrolling={isPanelOpen}
        theme={preferences.theme}
        windowSize={{ width, height }}
      >
        <AnalysisPane windowSize={{ width, height }}>
          <AnalysisView />
        </AnalysisPane>
        <SlidingPane isOpen={isPanelOpen} togglePanel={() => togglePane()}>
          <PaneContent>
            <AppHeader />
            <DataEntry onSubmit={() => togglePane(true)} />
          </PaneContent>
        </SlidingPane>
      </Container>
      <HelpTour isOpen={isTourOpen} onTourComplete={onTourComplete} />
    </>
  );
};

export const ReactPaceApp = () => (
  <PreferencesProvider>
    <ReactPace />
  </PreferencesProvider>
);
